/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    TextField,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Divider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TableSortLabel,
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import apiHeaders from "./apiHeaders";
import { handleSessionExpiredClose } from "./logout";

const DEBOUNCE_MS = 400;

function LogsGrid() {
    const { rilletUserId: routeUserId } = useParams();
    const rilletUserId = routeUserId || sessionStorage.getItem("rilletUserId");

    const [logs, setLogs] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [eventTypeFilter, setEventTypeFilter] = useState("");
    const [sortBy, setSortBy] = useState("timestamp");
    const [sortDescending, setSortDescending] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [selectedLog, setSelectedLog] = useState(null);
    const [copied, setCopied] = useState(false);

    const [dateInput, setDateInput] = useState(null);
    const [debouncedDate, setDebouncedDate] = useState(null);

    // Add a new ref right below your existing debounceRef:
    const debounceRef = useRef(null); // Existing search ref
    const dateDebounceRef = useRef(null); // New date ref

    const fetchLogs = useCallback(async () => {
        if (!rilletUserId) return;
        setLoading(true);
        setError("");
        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/GetLogs/${rilletUserId}`,
                {
                    method: "POST",
                    headers: {
                        ...apiHeaders,
                        Authorization: `${sessionStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({
                        pageNumber: page + 1,
                        pageSize: rowsPerPage,
                        searchTerm: debouncedSearch || null,
                        eventTypeFilter: eventTypeFilter || null,
                        sortBy: sortBy || null,
                        dateFilter: debouncedDate || null,
                        sortDescending,
                    }),
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }
            if (!response.ok) throw new Error("Failed to fetch logs.");

            const data = await response.json();
            setLogs(data.items ?? []);
            setTotalCount(data.totalCount ?? 0);
        } catch (err) {
            console.error(err);
            setError("An error occurred while fetching logs.");
            setLogs([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [rilletUserId, page, rowsPerPage, debouncedSearch, eventTypeFilter, debouncedDate, sortBy, sortDescending]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleDateChange = (newValue) => {
    setDateInput(newValue); // newValue is a dayjs object or null

    if (dateDebounceRef.current) clearTimeout(dateDebounceRef.current);
    
    dateDebounceRef.current = setTimeout(() => {
        // Format to string (e.g., 'YYYY-MM-DD') for your API, or null if cleared
        setDebouncedDate(newValue ? newValue.format('YYYY-MM-DD') : null);
        setPage(0);
    }, 800);
};

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            setDebouncedSearch(value);
        }, DEBOUNCE_MS);
    };

    const handleChangePage = (_, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleEventTypeChange = (e) => {
        setEventTypeFilter(e.target.value);
        setPage(0);
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDescending(!sortDescending);
        } else {
            setSortBy(column);
            setSortDescending(true);
        }
        setPage(0);
    };

    const formatTimestamp = (val) => {
        if (!val) return "";
        const d = new Date(val);
        return isNaN(d.getTime()) ? val : d.toLocaleString();
    };

    const truncateJson = (data, maxLen = 50) => {
        if (data == null) return "";
        const str = typeof data === "string" ? data : JSON.stringify(data);
        return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
    };

    const handleViewClick = (row) => {
        setSelectedLog(row);
        setCopied(false);
    };

    const handleCloseDialog = () => setSelectedLog(null);

    const handleCopyData = () => {
        if (!selectedLog?.data) return;
        const text = typeof selectedLog.data === "string"
            ? selectedLog.data
            : JSON.stringify(selectedLog.data, null, 2);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    if (loading && logs.length === 0) return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "calc(100vh - 64px)" }}>
            <CircularProgress size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Loading logs...
            </Typography>
        </Box>
    );

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Typography className="zamp-page-title" sx={{ mb: 2, textAlign: "center" }}>
                Connector Logs
            </Typography>

            <Paper elevation={6} sx={{ boxShadow: 4, borderRadius: 2, p: 4, maxWidth: 1250, width: "100%", mx: "auto", mt: 1 }}>
{/* Filters & Actions */}
<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
    
    {/* Left Side: Filters */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <TextField
            size="small"
            label="Search by Transaction ID"
            placeholder="Start typing to search"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Event Type</InputLabel>
            <Select
                value={eventTypeFilter}
                label="Event Type"
                onChange={handleEventTypeChange}
            >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Info">Info</MenuItem>
                <MenuItem value="Warn">Warn</MenuItem>
                <MenuItem value="Error">Error</MenuItem>
                <MenuItem value="Debug">Debug</MenuItem>
            </Select>
        </FormControl>
        
{/* New Date Filter */}
<LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
        label="Date"
        value={dateInput}
        onChange={handleDateChange}
        slotProps={{
            textField: {
                size: "small",
                InputLabelProps: { shrink: true }
            }
        }}
    />
</LocalizationProvider>
    </Box>

    {/* Right Side: Refresh Button */}
    <Tooltip title="Refresh Logs">
        <IconButton 
            onClick={fetchLogs} 
            color="primary" 
            sx={{ 
                border: "1px solid", 
                borderColor: "grey.300",
                backgroundColor: "white",
                "&:hover": { backgroundColor: "grey.100" }
            }}
        >
            <RefreshIcon />
        </IconButton>
    </Tooltip>
</Box>

                {error && (
                    <Typography color="error" sx={{ mb: 1, fontSize: 14 }}>{error}</Typography>
                )}

                <TableContainer sx={{ border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1, position: "relative" }}>
                    {loading && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255,255,255,0.7)",
                                zIndex: 2,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ height: 50, backgroundColor: "grey.400" }}>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Id</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                    <TableSortLabel active={sortBy === "transactionid"} direction={sortBy === "transactionid" && !sortDescending ? "asc" : "desc"} onClick={() => handleSort("transactionid")}>
                                        Transaction Id
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                    <TableSortLabel active={sortBy === "eventtype"} direction={sortBy === "eventtype" && !sortDescending ? "asc" : "desc"} onClick={() => handleSort("eventtype")}>
                                        Event Type
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                    <TableSortLabel active={sortBy === "timestamp"} direction={sortBy === "timestamp" && !sortDescending ? "asc" : "desc"} onClick={() => handleSort("timestamp")}>
                                        Timestamp
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Message</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16, borderRight: "1px solid rgba(224, 224, 224, 1)" }}>Data</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">
                                            No logs found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.map((row, idx) => (
                                    <TableRow key={row.id ?? idx} sx={{ backgroundColor: idx % 2 === 0 ? "white" : "grey.100" }}>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
                                            {row.id ?? ""}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            <Tooltip title={row.transactionId || ""}>
                                                <span>{row.transactionId || ""}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            <Tooltip title={row.eventType || ""}>
                                                <span>{row.eventType || ""}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", whiteSpace: "nowrap" }}>
                                            {formatTimestamp(row.timestamp)}
                                        </TableCell>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            <Tooltip title={row.message || ""}>
                                                <span>{row.message || ""}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ borderRight: "1px solid rgba(224, 224, 224, 1)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            <Tooltip title={row.data ? JSON.stringify(row.data, null, 2) : ""}>
                                                <span>{truncateJson(row.data)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View details">
                                                <IconButton size="small" color="primary" onClick={() => handleViewClick(row)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                />
            </Paper>

            {/* Detail Dialog */}
            <Dialog open={!!selectedLog} onClose={handleCloseDialog} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "grey.200", py: 2, px: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Log Details{selectedLog?.id ? ` — #${selectedLog.id}` : ""}
                    </Typography>
                    <IconButton onClick={handleCloseDialog} size="small"
                        sx={{ color: "grey.500", border: "1px solid", borderColor: "grey.300", backgroundColor: "white", "&:hover": { backgroundColor: "grey.100", color: "grey.800" } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ px: 3, py: 3 }}>
                    {selectedLog && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                                <DetailField label="Transaction Id" value={selectedLog.transactionId} />
                                <DetailField label="Customer Id" value={selectedLog.customerId} />
                                <DetailField label="Event Type" value={selectedLog.eventType} />
                                <DetailField label="Timestamp" value={formatTimestamp(selectedLog.timestamp)} />
                            </Box>

                            <Divider />

                            <DetailField label="Message" value={selectedLog.message} />

                            {selectedLog.stackTrace && (
                                <>
                                    <Divider />
                                    <DetailField label="Stack Trace" value={selectedLog.stackTrace} mono />
                                </>
                            )}

                            <Divider />

                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                        Data
                                    </Typography>
                                    <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                                        <IconButton size="small" onClick={handleCopyData}
                                            sx={{ color: "grey.500", border: "1px solid", borderColor: "grey.200", "&:hover": { color: "primary.main", borderColor: "primary.main" } }}
                                        >
                                            {copied ? <CheckIcon sx={{ fontSize: 16 }} color="success" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: "#f0f0f0",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        borderRadius: 2,
                                        p: 2.5,
                                        maxHeight: 350,
                                        overflow: "auto",
                                    }}
                                >
                                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.7 }}>
                                        {selectedLog.data
                                            ? (typeof selectedLog.data === "string" ? selectedLog.data : JSON.stringify(selectedLog.data, null, 2))
                                            : "—"}
                                    </pre>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

function DetailField({ label, value, mono }) {
    return (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", mb: 0.5 }}>
                {label}
            </Typography>
            {mono ? (
                <Box sx={{ backgroundColor: "#f0f0f0", border: "1px solid", borderColor: "grey.300", borderRadius: 2, p: 1.5 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                        {value || "—"}
                    </Typography>
                </Box>
            ) : (
                <Typography variant="body1" sx={{ color: value ? "text.primary" : "text.disabled" }}>
                    {value || "—"}
                </Typography>
            )}
        </Box>
    );
}

export default LogsGrid;
